/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("WebComponentWidget", ["FieldWidgetBase", "WidgetFactory"], function (context, cls) {
  // Applications that currently have a web-component-failure dialog open. Shared across all
  // WebComponentWidget instances so that when several components fail on the same form only
  // the first opens a Continue/Quit dialog; the others isolate silently. WeakSet -> no leak.
  const pendingFailureApps = new WeakSet();

  /**
   * WebComponent widget.
   * @class WebComponentWidget
   * @memberOf classes
   * @extends classes.FieldWidgetBase
   * @publicdoc Widgets
   */
  cls.WebComponentWidget = context.oo.Class(cls.FieldWidgetBase, function ($super) {
    return /** @lends classes.WebComponentWidget.prototype */ {
      $static: {
        gICAPIVersion: "1.0",
        focusEvent: context.constants.widgetEvents.focus,
        dataEvent: "wc_data",
        actionEvent: "wc_action",
        ready: "wc_ready"
      },

      __name: "WebComponentWidget",
      __dataContentPlaceholderSelector: cls.WidgetBase.selfDataContent,
      _webComponentType: null,
      /**
       * @type {Window}
       */
      _webComponentWindow: null,
      _webComponentProxy: null,
      _webComponentWindowKeyDown: null,
      _webComponentWindowClick: null,
      _webComponentWindowDblClick: null,
      _webComponentWindowContextMenu: null,
      _flushValue: "",
      _flushingData: false,
      _url: null,
      _isReady: false,
      _value: null,
      _stopOnError: true,
      // guards against stacking a second Continue/Quit dialog for the same component
      // while one is already open (error() can re-fire on repeated ICAPI throws).
      _failureDialogPending: false,
      // single reused inline error banner (see _showInlineError)
      _inlineErrorLayer: null,

      /**
       * @type HTMLElement
       */
      _iframeElement: null,

      /** @type {Object} */
      _properties: null, // webcomponent properties

      /**
       * @type {Boolean}
       */
      _isWebcomponentFocused: false,

      /**
       * Kept rendered off-screen while hidden (preserves internal state), so
       * it needs `inert` rather than `display: none`.
       * @inheritDoc
       */
      hidesOutOfView: function () {
        return true;
      },

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        if (!this._ignoreLayout) {
          this._layoutInformation = new cls.LayoutInformation(this);
          this._layoutInformation.shouldFillStack = true;
          this._layoutEngine = new cls.WebComponentLayoutEngine(this);
          this._layoutEngine._shouldFillHeight = true;
          // should have same rules that TextEdit and shouldn't support sizepolicy dynamic, so we override it
          this._layoutInformation.getSizePolicyConfig().dynamic = cls.SizePolicy.Initial();
          this._layoutInformation.getSizePolicyConfig().setAllShrinkable(true);
          this._layoutInformation.forcedMinimalWidth = 20;
          this._layoutInformation.forcedMinimalHeight = 20;
        }
      },

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this._iframeElement = this._element.getElementsByTagName("iframe")[0];
        cls.DOMElementHelper.on(this._iframeElement, "load.WebComponentWidget", this._onLoad.bind(this));
        this._properties = "{}";

        this._themeWatcher = context.ThemeService.whenThemeChanged(() => {
          this.syncThemeVariables();
        });
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._delegatesKeyboard(false);
        this._delegatesMouse(false);
        try {
          this._webComponentWindow.gICAPI = null;
        } catch (e) {}
        this._webComponentWindow = null;
        this._inlineErrorLayer = null;
        cls.DOMElementHelper.off(this._iframeElement, "load.WebComponentWidget");
        this._iframeElement = null;
        if (this._themeWatcher) {
          this._themeWatcher();
          this._themeWatcher = null;
        }
        $super.destroy.call(this);
      },

      /**
       * Handler for onData when iframe is loaded
       * @private
       */
      _onReadyData: function () {
        if (this.value !== this._flushValue) {
          this._toICAPI("onData", this._value ? this._value : "");
        }
      },

      /**
       * Handler called once iframe has loaded its content
       * @private
       */
      _onLoad: function () {
        if (this.getUrl()) {
          if (this._webComponentType === "api") {
            this._injectApi();
            this._delegatesMouse(true);
            this._delegatesKeyboard(true);
          } else if (this._webComponentType === "url") {
            this.triggerValueChangedEvent(this.getValue());
          }
          this._onReady();
        }
      },

      /**
       * Handler for onFocus when iframe is loaded
       * @private
       */
      _onReadyFocus: function () {
        if (this._uiWidget && this._uiWidget.getFocusedWidget() !== this) {
          this.triggerValueChangedEvent(this.getValue());
          this._updateWebcomponentFocus(false);
        } else {
          if (this._iframeElement !== document.activeElement) {
            this.domFocus();
          }
          this._updateWebcomponentFocus(true);
        }
      },

      /**
       * Handler for onBlur when iframe is loaded
       * @private
       */
      _onReadyBlur: function () {
        this.getValue();
        this.emit(context.constants.widgetEvents.blur);
        this._updateWebcomponentFocus(false);
      },

      /**
       * OnStateChanged handler once iframe is ready
       * @param {boolean} active - form state
       * @param {string} dialogType - form display type (display, input ...)
       * @private
       */
      _onReadyStateChanged: function (active, dialogType) {
        this._toICAPI(
          "onStateChanged",
          JSON.stringify({
            active: Number.parseInt(active),
            dialogType: dialogType.toString()
          })
        );
      },

      /**
       * Set the property of the webcomponent
       * @param {string} property - stringified json object of properties
       * @private
       */
      setProperty: function (property) {
        if (this._isReady) {
          this._toICAPI("onProperty", property);
        } else {
          this.when(cls.WebComponentWidget.ready, this._onReadyProperty.bind(this, property));
        }
      },

      /**
       * When the iframe is loaded handler
       * @private
       */
      _onReady: function () {
        this._isReady = true;
        this.emit(context.constants.widgetEvents.ready);
      },

      /**
       * onProperty handler once iframe is ready
       * @param {string} property - stringified json object of properties
       * @private
       */
      _onReadyProperty: function (property) {
        this._toICAPI("onProperty", property);
      },

      /**
       * Choose what theme variables to use in your webcomponent, it auto refreshes the list
       * @param {string[]} themeVariables - list of variables you want to use in your webcomponent
       */
      injectThemeVariables: function (themeVariables) {
        this._syncedThemeVariables = themeVariables;
        this.syncThemeVariables();
      },

      /**
       * Synchronize the chosen GBC theme variables as css variables in the webcomponent
       */
      syncThemeVariables: function () {
        const themeVarsToInject = this._syncedThemeVariables || [];
        themeVarsToInject.forEach((themeVar) => {
          this._webComponentWindow.document.documentElement.style.setProperty(`--${themeVar}`, gbc.ThemeService.getValue(themeVar));
        });
      },

      /**
       * Inject the API on the webcomponent
       * @returns {boolean} false if not applicable
       * @private
       */
      _injectApi: function () {
        //Add a new proxy for this webcomponent
        context.WebComponentService.setProxy(this.getUniqueIdentifier());
        this._webComponentProxy = context.WebComponentService.getProxy(this.getUniqueIdentifier());
        //Get the content of the iframe window to put api on
        this._webComponentWindow = this._iframeElement.contentWindow;

        // Inject some CSS variables
        this.syncThemeVariables();

        try {
          this._webComponentWindow.gICAPIVersion = cls.WebComponentWidget.gICAPIVersion;
          // Bind WebComponent API to the iframe
          this._webComponentWindow.gICAPI = this._gICAPI();
          // Tell the WebComponent that host is ready
          if (this._webComponentWindow.onICHostReady) {
            this._webComponentWindow.onICHostReady("1.0");
          } else {
            context.LogService.gICAPI.error("onICHostReady no present in webcomponent, cannot continue!");
            this.webComponentNotFound();
            return false;
          }
        } catch (e) {
          this.error(e, true);
          return false;
        }
        this.emit(cls.WebComponentWidget.ready);
      },

      /**
       * Api object to bind to the webcomponent window.
       * It will be used inside the webcomponent to interact with the VM
       * @private
       */
      _gICAPI: function () {
        return {
          SetFocus: function () {
            // Generates a focus change request. The focus is entirely managed by the runtime system
            this._webComponentProxy.setFocus(this);
          }.bind(this),
          SetData: function (dataStr) {
            this._webComponentProxy.setData(this, dataStr);
          }.bind(this),
          Action: function (actionName) {
            this._webComponentProxy.action(this, actionName);
          }.bind(this),
          UseGbcThemeVariables: function (variableArray) {
            this._webComponentProxy.useGbcThemeVariables(this, variableArray);
          }.bind(this),
          version: "1.0" // Legacy, but mostly not used
        };
      },

      /**
       * Delegate the keyboard management to GBC keyboard service (for accelerators and so)
       * @warning This works only if webcomponent is on the same domain as GBC application
       * @param {boolean} delegate - true to delegate it, false to let the webcomponent live by itself
       * @private
       */
      _delegatesKeyboard: function (delegate) {
        try {
          if (!this._webComponentWindow) {
            this._webComponentWindow = this._iframeElement.contentWindow;
          }
          if (delegate) {
            // If handler already exists, remove it before creating it again
            if (this._webComponentWindowKeyDown) {
              this._webComponentWindow.document.body.removeEventListener("keydown", this._webComponentWindowKeyDown);
            }
            // Define the keyDown handler
            this._webComponentWindowKeyDown = function (evt) {
              this.emit(gbc.constants.widgetEvents.webcomponentKeyDown, evt);
            }.bind(this);
            // Bind the iframe keyDown event to the keyDown handler
            this._webComponentWindow.document.body.addEventListener("keydown", this._webComponentWindowKeyDown);
          } else {
            if (this._webComponentWindowKeyDown) {
              this._webComponentWindow.document.body.removeEventListener("keydown", this._webComponentWindowKeyDown);
              this._webComponentWindowKeyDown = null;
            }
          }
        } catch (error) {
          context.LogService.gICAPI.warn("Can not use GBC accelerators in a cross-domain Webcomponents (" + this._url + ")");
        }
      },

      /**
       * Delegate the mouse management to GBC mouse service (click, contextmenu)
       * @warning This works only if webcomponent is on the same domain as GBC application
       * @param {boolean} delegate - true to delegate it, false to let the webcomponent live by itself
       * @private
       */
      _delegatesMouse: function (delegate) {
        try {
          if (!this._webComponentWindow) {
            this._webComponentWindow = this._iframeElement.contentWindow;
          }
          if (delegate) {
            // If handler already exists, remove it before creating it again
            if (this._webComponentWindowClick) {
              this._webComponentWindow.document.body.removeEventListener("click", this._webComponentWindowClick);
            }
            if (this._webComponentWindowDblClick) {
              this._webComponentWindow.document.body.removeEventListener("dblclick", this._webComponentWindowDblClick);
            }
            if (this._webComponentWindowContextMenu) {
              this._webComponentWindow.document.body.removeEventListener("contextmenu", this._webComponentWindowContextMenu);
            }
            // Define the mouse events handlers
            this._webComponentWindowClick = function (evt) {
              context.MouseService.saveMousePosition(evt, this.getElement());
            }.bind(this);
            this._webComponentWindowDblClick = function (evt) {
              context.MouseService.saveMousePosition(evt, this.getElement());
            }.bind(this);
            this._webComponentWindowContextMenu = function (evt) {
              context.MouseService.saveMousePosition(evt, this.getElement());
            }.bind(this);
            // Bind the iframe mouse events to their respective handlers
            this._webComponentWindow.document.body.addEventListener("click", this._webComponentWindowClick);
            this._webComponentWindow.document.body.addEventListener("dblclick", this._webComponentWindowDblClick);
            this._webComponentWindow.document.body.addEventListener("contextmenu", this._webComponentWindowContextMenu);
          } else {
            if (this._webComponentWindowClick) {
              this._webComponentWindow.document.body.removeEventListener("click", this._webComponentWindowClick);
              this._webComponentWindowClick = null;
            }
            if (this._webComponentWindowDblClick) {
              this._webComponentWindow.document.body.removeEventListener("dblclick", this._webComponentWindowDblClick);
              this._webComponentWindowDblClick = null;
            }
            if (this._webComponentWindowContextMenu) {
              this._webComponentWindow.document.body.removeEventListener("contextmenu", this._webComponentWindowContextMenu);
              this._webComponentWindowContextMenu = null;
            }
          }
        } catch (error) {
          context.LogService.gICAPI.warn("Can not propagate mouse events to GBC context in a cross-domain Webcomponents (" + this._url + ")");
        }
      },

      /**
       * Function to transmit gICAPI orders to webcomponent
       * @param {string} verb - onData, onProperty, onChangeState, onFlushData
       * @param {string=} args - arguments passed to the gICAPI if any
       * @private
       */
      _toICAPI: function (verb, args) {
        try {
          let arg = args;
          if (verb === "onData" && arg === null) {
            arg = [null];
          } else {
            if (!arg && arg !== false && arg !== "" && arg !== 0) {
              arg = [];
            }
            if (arg.prototype !== Array) {
              arg = [arg];
            }
          }
          if (this._webComponentWindow && this._webComponentWindow.gICAPI && this._webComponentWindow.gICAPI[verb]) {
            this._webComponentWindow.gICAPI[verb].apply(this._webComponentWindow.gICAPI, arg);
            if (verb === "onFlushData") {
              this._flushingData = false;
            }
          }
        } catch (e) {
          this.error(e);
        }
      },

      /**
       * Handler to execute a function only once the component is ready
       * @param callback
       * @protected
       */
      _onReadyExecute: function (callback) {
        if (this._isReady) {
          callback();
        } else {
          this.when(
            cls.WebComponentWidget.ready,
            function () {
              callback();
            }.bind(this)
          );
        }
      },

      /**
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (keyString === "home" || keyString === "end") {
          return false; // consider home/end key as not processed to keep default from webcomponent
        } else {
          return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
        }
      },

      /**
       * Report an error in webcomponent
       * @param e - error object
       * @param {boolean} stop - will stop the application if true
       */
      error: function (e, stop) {
        context.LogService.gICAPI.error(gbc.I18NService.t("gwc.app.webcompError.message"));
        context.LogService.gICAPI.error(">>> " + ": " + e.toString(), e);
        if (this._stopOnError || stop) {
          this.webComponentNotFound(
            gbc.I18NService.t("gwc.app.webcompError.message") + ' : "' + e.toString() + '"\n' + gbc.I18NService.t("gwc.app.webcompError.seeConsole")
          );
        } else {
          this._showInlineError(
            gbc.I18NService.t("gwc.app.webcompError.message") + ' : "' + e.toString() + '"\n' + gbc.I18NService.t("gwc.app.webcompError.seeConsole")
          );
        }
      },

      /**
       * Render a dismissible inline error banner inside the webcomponent iframe,
       * leaving the form/application running (failure isolated). Shared by the
       * non-fatal error() path and the "Continue" choice.
       * @param {string} text - the error text to display
       * @private
       */
      _showInlineError: function (text) {
        if (!this._webComponentWindow) {
          return;
        }
        try {
          const body = this._webComponentWindow.document.querySelector("body");
          if (!body) {
            return;
          }
          // reuse a single banner so repeated failures don't accumulate DOM nodes;
          // role=alert lets a screen reader announce the (updated) message.
          let errorLayer = this._inlineErrorLayer;
          if (!errorLayer || !body.contains(errorLayer)) {
            errorLayer = document.createElement("div");
            errorLayer.setAttribute("role", "alert");
            errorLayer.addEventListener("click", function () {
              this.style.display = "none";
            });
            this._inlineErrorLayer = errorLayer;
            body.appendChild(errorLayer);
          }
          errorLayer.setAttribute(
            "style",
            "position: fixed;top: 0; white-space: pre-wrap; color: #721c24; background-color: #f8d7da;border-color: #f5c6cb;border: 1px solid; padding: 10px; border-radius: .25rem;"
          );
          errorLayer.textContent = text;
        } catch {
          // cross-domain webcomponent: its document is not accessible (same restriction as
          // _delegatesKeyboard/_delegatesMouse), so the inline banner can't be injected. The
          // failure is still surfaced via the console and the Continue/Quit dialog.
          context.LogService.gICAPI.warn("Can not show inline error in a cross-domain Webcomponents (" + this._url + ")");
        }
      },

      /**
       *@inheritDoc
       */
      setFocus: function (fromMouse = false, preventScroll = false) {
        if (!this.hasFocus() || !this._isWebcomponentFocused) {
          // Call API only if the widget is not focus yet or never focused before
          if (this._isReady) {
            this.domFocus(preventScroll); //force focus to blur other elements
            this._updateWebcomponentFocus(true);
          } else {
            this.when(cls.WebComponentWidget.ready, this._onReadyFocus.bind(this));
          }
          $super.setFocus.call(this, fromMouse, preventScroll);
        }
      },

      /**
       * @inheritDoc
       */
      hasDOMFocus: function () {
        return document.activeElement === this._iframeElement;
      },

      /**
       * @inheritDoc
       */
      domFocus: function (preventScroll = false) {
        if (this._iframeElement) {
          cls.DOMElementHelper.domFocus(this._iframeElement, preventScroll);
        }
      },

      /**
       * Tell the WebComponent that it lost the Focus on it
       * @publicdoc
       */
      loseFocus: function () {
        $super.loseFocus.call(this);
        this._onReadyExecute(
          function () {
            this._onReadyBlur();
          }.bind(this)
        );
      },

      /**
       * Called when the WC formfield's state has changed
       * @param {boolean} active - form state
       * @param {string} dialogType - form display type (display, input ...)
       * @publicdoc
       */
      onStateChanged: function (active, dialogType) {
        this._onReadyExecute(
          function () {
            this._onReadyStateChanged(active, dialogType);
          }.bind(this)
        );
      },

      /**
       * Defines the address of the WebComponent
       * @param {string} url - address
       * @publicdoc
       */
      setUrl: function (url) {
        this._url = url;
        url = url ? url : "about:blank";
        this._iframeElement.setAttribute("src", url);
      },

      /**
       * Get the address of the WebComponent
       * If the webcomponent is url based, it will work only if url is on same domain as application
       * @returns {string} address of the webcomponent
       */
      getUrl: function () {
        const url = this._iframeElement.getAttribute("src") || this._url;
        let contentUrl = false;
        if (this._webComponentType === "url") {
          try {
            contentUrl = this._iframeElement.contentWindow && this._iframeElement.contentWindow.location.href;
          } catch (e) {
            contentUrl = false;
          }
        }
        return contentUrl ? contentUrl : url;
      },

      /**
       * @inheritDoc
       */
      setValue: function (value, fromVM = false, cursorPosition = null) {
        const empty = value === "";
        // Value should be a string to fit GDC/VM specs
        if (typeof value !== "string") {
          value = JSON.stringify(value);
        }
        this._value = value;
        if (this._webComponentType === "url") {
          this.setUrl(empty ? "" : value);
        } else {
          this._onReadyExecute(
            function () {
              this._toICAPI("onData", this._value ? this._value : "");
            }.bind(this)
          );
        }
      },

      /**
       * Get The value of the webComponent
       * @returns {string} value or url of the webcomponent
       */
      getValue: function () {
        this.flushWebcomponentData();
        const result = this._webComponentType === "api" ? (this._flushValue ? this._flushValue : this._value) : this.getUrl();
        this._flushValue = null; // reset value
        return result;
      },

      /**
       * Define the type of component
       * @param {string} type - should be 'api' or 'url'
       */
      setWebComponentType: function (type) {
        this._webComponentType = type;
      },

      /**
       * Get the type of component
       * @return {?string} type - should be 'api' or 'url'
       */
      getWebComponentType: function () {
        return this._webComponentType;
      },

      /**
       * Force webcomponent to get data
       * @public
       */
      flushWebcomponentData: function () {
        if (!this._flushingData) {
          this._flushingData = true;
          this._toICAPI("onFlushData");
        }
      },

      /**
       * Update & filter focus (true/false) flag on Webcomponent side
       * @param focus
       * @protected
       */
      _updateWebcomponentFocus: function (focus) {
        if (this._isWebcomponentFocused !== focus) {
          this._toICAPI("onFocus", focus);
          this._isWebcomponentFocused = focus;
        }
      },

      /**
       * Best-effort discovery of assets the webcomponent iframe failed to download
       * (e.g. a 404 on its bootstrap script), read from the iframe Resource Timing.
       * The HTTP status is only exposed by Chromium (PerformanceResourceTiming.responseStatus);
       * on other engines this returns [] and only the generic message is shown. Full asset
       * diagnostics and retry are handled separately (resilient asset loading).
       * @returns {string[]} "<url> (<status>)" for each failed asset
       * @private
       */
      _getFailedAssets: function () {
        const failed = [];
        try {
          const perf = this._webComponentWindow?.performance;
          if (perf?.getEntriesByType) {
            for (const entry of perf.getEntriesByType("resource")) {
              if (entry.responseStatus && entry.responseStatus >= 400) {
                failed.push(entry.name + " (" + entry.responseStatus + ")");
              }
            }
          }
        } catch {
          // iframe performance not accessible / unsupported -> best effort, ignore
        }
        return failed;
      },

      /**
       * Stop VMApplication with a message
       * @param {string=} message - message to give to the app close (default is defined in locales)
       * @public
       */
      webComponentNotFound: function (message) {
        if (context.bootstrapInfo.ignoreWebComponentFails || this._failureDialogPending) {
          return;
        }
        let reason = message || gbc.I18NService.t("gwc.app.webcompNotFound.message");
        // surface the console-only root cause (e.g. a 404 on the webcomponent bootstrap
        // asset) in the dialog. Full asset diagnostics/retry are handled separately.
        const failedAssets = this._getFailedAssets();
        if (failedAssets.length > 0) {
          reason += "\n" + failedAssets.join("\n");
        }
        // Resolve the application that OWNS this web component, not merely the session-current
        // one: a deferred ICAPI error can fire on a background app while a RUN child is current,
        // and gbc.confirm is async, so we must fail THIS app — not whatever is current later.
        const session = gbc.SessionService.getCurrent();
        const failingApp = session && (session.getApplicationByIdentifier(this.getApplicationIdentifier()) || session.getCurrentApplication());
        // No owning application to fail (degenerate state): just isolate the component. A
        // Continue/Quit dialog would be pointless — its Quit would have nothing to terminate.
        if (!failingApp) {
          this._showInlineError(reason);
          return;
        }
        // If another component on the same application already opened the decision dialog,
        // don't stack a second one — isolate this component instead. The single dialog governs
        // the whole app: Quit kills the app (and every component with it), Continue isolates all.
        if (pendingFailureApps.has(failingApp)) {
          this._showInlineError(reason);
          return;
        }
        const quit = () => {
          failingApp.fail(reason);
        };
        const clearPending = () => {
          this._failureDialogPending = false;
          pendingFailureApps.delete(failingApp);
        };
        // rather than silently failing the app (which, for a child program
        // launched via RUN, just drops the user back to the parent menu with no
        // explanation), let the user decide. gbc.confirm is a system widget, so the
        // dialog shows in every context (top-level and child programs). Continue is the
        // default/escape (non-destructive); Quit must be an explicit click.
        this._failureDialogPending = true;
        pendingFailureApps.add(failingApp);
        gbc
          .confirm(reason, gbc.I18NService.t("gwc.app.webcompFail.title"), [
            { text: gbc.I18NService.t("gwc.app.webcompFail.continue"), value: "continue", isDefault: true, isCancel: true },
            { text: gbc.I18NService.t("gwc.app.webcompFail.quit"), value: "quit" }
          ])
          .then(
            (choice) => {
              clearPending();
              if (choice === "quit") {
                quit();
              } else {
                // continue: keep the application running with the failed component isolated
                this._showInlineError(reason);
              }
            },
            () => {
              // Two-arg then(): this rejection handler covers ONLY a gbc.confirm build/show
              // failure, never a throw from the fulfillment branch above — so a failure to
              // render the "Continue" banner can never turn the user's Continue into a Quit.
              // On a genuine dialog failure, fall back to the original integrity behaviour.
              clearPending();
              quit();
            }
          );
      }
    };
  });
  cls.WidgetFactory.registerBuilder("WebComponent", cls.WebComponentWidget);
});
